import {useState} from 'react';
import {useDispatch, useStore} from 'react-redux';
import {Outlet} from 'react-router-dom';

import {refreshAccessToken} from '@dms/api/base';
import {browserStorageKey} from '@dms/config';
import {getWorkspaceFromUri, redirectToLogin} from '@dms/routes';
import {CommonSkeleton} from '@dms/shared';

import {useOnMount} from 'shared/web';

type RefreshDispatch = Parameters<typeof refreshAccessToken>[0];

/**
 * Resolves the session tokens before any authenticated request fires.
 *
 * The access token lives in sessionStorage, so every new tab starts without one. The API gateway
 * rejects requests without an Authorization header with 400 (not 401), which never reaches the
 * 401 refresh branch in the base queries — so the token must be in place before `Initialize`
 * mounts its queries.
 *
 * Review apps and Cypress need the guard too: Cypress seeds the tokens before the visit, and a
 * token-less Cypress visit must reach the IAM login like a real user.
 */
export function AuthBootstrap() {
  const {workspace} = getWorkspaceFromUri();
  const dispatch = useDispatch<RefreshDispatch>();
  const store = useStore();

  const [isSessionReady, setIsSessionReady] = useState(
    () => sessionStorage.getItem(browserStorageKey.ACCESS_TOKEN) !== null
  );

  useOnMount(() => {
    if (isSessionReady) {
      return;
    }

    if (localStorage.getItem(browserStorageKey.REFRESH_TOKEN) === null) {
      redirectToLogin({workspace});
      return;
    }

    // On failure refreshAccessToken clears the tokens and redirects to login itself.
    void refreshAccessToken(dispatch, store.getState).then((isRefreshed) => {
      if (isRefreshed) {
        setIsSessionReady(true);
      }
    });
  });

  if (!isSessionReady) {
    return <CommonSkeleton />;
  }

  return <Outlet />;
}
